import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Player } from 'fl-player-interface';
import { ResourceVm } from 'qp-ui-interface';
import { DataService } from '../../data.service';
import { RecommendationsService } from '../../shared/services/firstlight/recommendations/recommendations.service';
import { NavigationLibraryService } from '../../shared/services/navigation/navigation.service';
import { RoutingService } from '../../shared/services/routing/routing.service';
import { StorefrontService } from '../../storefront/storefront.service';
import { PlaybackNewrelicService } from 'src/app/analytics/newrelic/service/playback/playback-newrelic.service';

@Component({
  selector: 'app-play-next',
  templateUrl: './play-next.component.html',
  styleUrls: ['./play-next.component.scss'],
})
export class PlayNextComponent implements OnInit, OnDestroy {
  resourceVmUpNext: ResourceVm;
  timeLeft = 30;
  private alreadyClicked = false;
  private currentResource!: ResourceVm | undefined;
  private interval: any;

  constructor(
    private readonly storefrontService: StorefrontService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      resource: ResourceVm;
      tabId: string;
      player: Player;
    },
    private readonly dialogRef: MatDialogRef<PlayNextComponent>,
    private readonly routingService: RoutingService,
    private readonly recommendationService: RecommendationsService,
    private readonly navService: NavigationLibraryService,
    private readonly newrelicPlayback: PlaybackNewrelicService,
    private dataService: DataService
  ) {}

  upNextClose(): void {
    this.pauseTimer();
    this.dialogRef.close();
  }

  ngOnInit(): void {
    if (this.data === null || this.data === undefined) {
      return;
    }

    this.currentResource = this.data.resource;
    this.startTimer();
    this.fetchUpNext();
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  /**
   * Click on up-next card
   *
   * @returns
   */
  async upNextClick(): Promise<void> {
    this.pauseTimer();
    if (this.data.resource.id !== this.resourceVmUpNext.id && !this.alreadyClicked) {
      try {
        this.alreadyClicked = true;
        this.timeLeft--;
        const isSeries = ['tvseries', 'tvseason', 'tvepisode'].includes(this.data?.resource?.contentType);
        if (isSeries) {
          this.newrelicPlayback.sendNextEpisode(); // User clicks on next episode
        }
        await this.routingService.navigateToPlayerWithValidations(this.resourceVmUpNext, false);
      } catch (_) {
        this.dialogRef.close('Not entitled to play');
        this.alreadyClicked = false;
      }
    } else {
      this.dialogRef.close();
    }
    this.dataService.playbackStartTime = new Date().getTime();
    this.upNextClose();
  }

  setUpNextContent(result: ResourceVm): void {
    if (!result || result.id === this.currentResource.id) {
      this.stopTimer();
      this.upNextClose();
    } else {
      this.resourceVmUpNext = result;
    }
  }

  private startTimer(): void {
    this.navService.focusContainer('play-next__card', true, 0);
    this.interval = setInterval(() => {
      if (this.timeLeft > 1) {
        this.timeLeft--;
      } else if (this.timeLeft === 1) {
        this.upNextClick();
      }
    }, 1000);
  }

  private pauseTimer(): void {
    clearInterval(this.interval);
  }

  /**
   * shared content to playback (same as the URL data)
   */

  private stopTimer() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  private fetchUpNext(): void {
    if (this.currentResource.contentType.includes('serie') || this.currentResource.contentType.includes('episode')) {
      this.storefrontService
        .fetchUpNextContent(
          this.currentResource.seriesId,
          this.currentResource.contentGenre,
          this.currentResource.contentType,
          this.currentResource.providerName,
          this.currentResource.seasonNumber,
          this.currentResource.episodeNumber
        )
        .then((result: ResourceVm) => {
          if (result.seriesId !== this.currentResource.seriesId) {
            this.upNextClose();
          } else {
            this.setUpNextContent(result);
            this.navService.focusPage('focusable-container');
          }
        })
        .catch(() => {
          this.upNextClose();
        });
    } else {
      this.recommendationService
        .fetchMoreLikeThis(this.currentResource.id, this.currentResource.contentType, this.currentResource.contentGenre, '16x9')
        .then((items) => {
          this.setUpNextContent(items.resources[0]);
          this.navService.focusPage('focusable-container');
        })
        .catch(() => {
          this.upNextClose();
        });
    }
  }
}
